// src/pages/Auth.jsx
// Single page handles both Login and Signup with a tab toggle
// Route it as:  <Route path="/login" element={<Auth />} />
//               <Route path="/signup" element={<Auth defaultTab="signup" />} />

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function Auth({ defaultTab = 'login' }) {
  const [tab,      setTab     ] = useState(defaultTab); // 'login' | 'signup'
  const [email,    setEmail   ] = useState('');
  const [password, setPassword] = useState('');
  const [name,     setName    ] = useState('');
  const [error,    setError   ] = useState('');
  const [success,  setSuccess ] = useState('');
  const [loading,  setLoading ] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);

    try {
      if (tab === 'signup') {
        // Create new account
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name } }, // store name in user metadata
        });
        if (error) throw error;
        setSuccess('Account created! Check your email to confirm, then log in.');
      } else {
        // Log in to existing account
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate('/dashboard'); // redirect after login
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
  setLoading(true);
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: 'https://thetradeclass.com/dashboard',
    },
  });
  if (error) { setError(error.message); setLoading(false); }
}

  async function handleForgotPassword() {
    if (!email) return setError('Enter your email above first.');
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setSuccess('Password reset email sent! Check your inbox.');
    setLoading(false);
  }

  // ── styles ──
  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#ffffff', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box',
  };
  const btnPrimary = {
    width: '100%', padding: '12px', borderRadius: '10px',
    backgroundColor: loading ? 'rgba(16,185,129,0.5)' : '#10b981',
    color: '#fff', border: 'none', fontSize: '14px',
    fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'block', textAlign: 'center', fontSize: '22px', fontWeight: 700, color: '#fff', textDecoration: 'none', marginBottom: '32px' }}>
          TheTrade<span style={{ color: '#10b981' }}>Class</span>
        </Link>

        {/* Card */}
        <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '32px' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '4px', marginBottom: '28px' }}>
            {['login', 'signup'].map(t => (
              <button key={t} onClick={() => { setTab(t); setError(''); setSuccess(''); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '8px', border: 'none',
                  fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                  backgroundColor: tab === t ? 'rgba(16,185,129,0.2)' : 'transparent',
                  color: tab === t ? '#10b981' : '#6b7280',
                }}>
                {t === 'login' ? 'Log in' : 'Sign up'}
              </button>
            ))}
          </div>

          {/* Google button */}
          <button onClick={handleGoogle} disabled={loading} style={{
            width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
            backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '13px',
            cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
          }}>
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5c10.5 0 20-7.6 20-20.5 0-1.4-.1-2.7-.4-4v-.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5c-7.7 0-14.3 4.4-17.7 10.2z"/>
              <path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 37 26.8 38 24 38c-5.2 0-9.6-2.9-11.3-7l-6.5 5C9.8 41.2 16.4 45.5 24 45.5z"/>
              <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2c-.4.3 5.7-4.2 5.7-13.3 0-1.4-.1-2.7-.4-4v-.5z"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <span style={{ fontSize: '11px', color: '#4b5563' }}>or with email</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {tab === 'signup' && (
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '5px' }}>Full name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="John Smith" required style={inputStyle} />
              </div>
            )}

            <div>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '5px' }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com" required style={inputStyle} />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Password</label>
                {tab === 'login' && (
                  <button type="button" onClick={handleForgotPassword}
                    style={{ fontSize: '11px', color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Forgot password?
                  </button>
                )}
              </div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min. 6 characters" required minLength={6} style={inputStyle} />
            </div>

            {/* Error / Success messages */}
            {error   && <div style={{ fontSize: '12px', color: '#f87171', backgroundColor: 'rgba(239,68,68,0.1)', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)' }}>{error}</div>}
            {success && <div style={{ fontSize: '12px', color: '#6ee7b7', backgroundColor: 'rgba(16,185,129,0.1)', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.2)' }}>{success}</div>}

            <button type="submit" disabled={loading} style={{ ...btnPrimary, marginTop: '4px' }}>
              {loading ? 'Please wait…' : tab === 'login' ? 'Log in' : 'Create account'}
            </button>
          </form>
        </div>

        {/* Bottom link */}
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#4b5563', marginTop: '20px' }}>
          {tab === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => { setTab(tab === 'login' ? 'signup' : 'login'); setError(''); setSuccess(''); }}
            style={{ color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }}>
            {tab === 'login' ? 'Sign up free' : 'Log in'}
          </button>
        </p>

        <p style={{ textAlign: 'center', fontSize: '11px', color: '#374151', marginTop: '12px' }}>
          By continuing you agree to our{' '}
          <Link to="/terms" style={{ color: '#4b5563' }}>Terms</Link> and{' '}
          <Link to="/privacy" style={{ color: '#4b5563' }}>Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}